import React, { useState } from "react";
import { services } from "@/data/serviceCatalog";
import { AA } from "@/constants/testIds";
import { Plus, Minus } from "@phosphor-icons/react";

export default function Services() {
  const [activePillar, setActivePillar] = useState(0);
  const [openItem, setOpenItem] = useState(services[0].items[0].key);

  const selectPillar = (i) => {
    if (i < 0 || i >= services.length) return;
    setActivePillar(i);
    setOpenItem(services[i].items[0].key);
  };

  const goToDrone = () => {
    const el = document.getElementById("drone");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const pillar = services[activePillar];

  return (
    <section id="services" className="relative bg-[#0A0A0A] py-24 lg:py-32 border-t border-white/5">
      <div className="absolute inset-0 bp-grid-fine opacity-50 pointer-events-none" />
      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-3xl">
            <div className="label-mono mb-5">02 / Capabilities</div>
            <h2 className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              Four disciplines.<br />
              <span className="text-[#FF4500]">One control network.</span>
            </h2>
          </div>
          <p className="text-white/60 max-w-md text-[15px] leading-relaxed">
            Survey crews and drone pilots cross-deployed on the same project datum — so the
            stake in the ground and the point cloud in the sky agree.
          </p>
        </div>

        {/* Pillar tabs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 hairline border-b-0">
          {services.map((p, i) => (
            <button
              key={p.pillarKey}
              data-testid={`pillar-tab-${p.pillarKey}`}
              onClick={() => selectPillar(i)}
              className={`text-left p-6 border-b sm:border-b-0 sm:border-r border-white/10 last:border-r-0 transition-colors ${
                activePillar === i
                  ? "bg-[#FF4500] text-[#0A0A0A]"
                  : "bg-transparent text-white hover:bg-white/5"
              }`}
            >
              <div
                className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-2 ${
                  activePillar === i ? "text-[#0A0A0A]/70" : "text-white/40"
                }`}
              >
                {String(i + 1).padStart(2, "0")} · Pillar
              </div>
              <div className="font-display font-bold text-xl tracking-tight">{p.pillar}</div>
            </button>
          ))}
        </div>

        {/* Drone pillar tab is separate */}
        <DronePillarTab onSelect={goToDrone} />

        {/* Active pillar content */}
        {pillar && (
          <div className="grid lg:grid-cols-12 gap-0 hairline border-t-0">
            <div className="lg:col-span-4 p-8 lg:p-10 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#121212]">
              <div className="label-mono mb-4">
                {String(activePillar + 1).padStart(2, "0")} / {pillar.pillarKey}
              </div>
              <h3 className="font-display font-bold text-white text-2xl tracking-tight mb-4">
                {pillar.pillar}
              </h3>
              <p className="text-white/65 text-[15px] leading-relaxed">{pillar.description}</p>
              <div className="mt-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40">
                {pillar.items.length} services →
              </div>
            </div>

            <div
              className="lg:col-span-8"
              data-testid={AA.serviceAccordion}
            >
              {pillar.items.map((it, idx) => {
                const open = openItem === it.key;
                return (
                  <div
                    key={it.key}
                    className="border-b border-white/10 last:border-b-0"
                    data-testid={AA.serviceItem(it.key)}
                  >
                    <button
                      onClick={() => setOpenItem(open ? null : it.key)}
                      className="w-full text-left px-6 lg:px-10 py-6 flex items-start justify-between gap-6 hover:bg-white/[0.02] transition-colors"
                    >
                      <div className="flex items-start gap-5">
                        <span className="font-mono text-[11px] tracking-[0.22em] text-[#FF4500] pt-1">
                          [{String(idx + 1).padStart(2, "0")}]
                        </span>
                        <span className="font-display font-bold text-white text-lg sm:text-xl tracking-tight">
                          {it.title}
                        </span>
                      </div>
                      <span className="text-white/60 pt-1">
                        {open ? <Minus size={20} /> : <Plus size={20} />}
                      </span>
                    </button>
                    {open && (
                      <div className="px-6 lg:px-10 pb-7 pl-[72px] lg:pl-[88px]">
                        <p className="text-white/70 text-[15px] leading-relaxed max-w-3xl">
                          {it.body}
                        </p>
                        {it.bullets && (
                          <ul className="mt-4 grid sm:grid-cols-2 gap-2 max-w-3xl">
                            {it.bullets.map((b) => (
                              <li
                                key={b}
                                className="flex items-start gap-2 text-white/60 text-[14px]"
                              >
                                <span className="text-[#FF4500] font-mono mt-1">▸</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function DronePillarTab({ onSelect }) {
  return (
    <div className="hairline border-t-0 border-b-0 -mt-px">
      <button
        onClick={onSelect}
        data-testid="pillar-tab-drone"
        className="w-full text-left p-6 transition-colors bg-transparent text-white hover:bg-[#0066FF]/15"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase mb-2 text-white/40">
              04 · Pillar
            </div>
            <div className="font-display font-bold text-xl tracking-tight">Drone Services</div>
          </div>
          <div className="font-mono text-xs tracking-[0.2em] uppercase opacity-80 text-[#0066FF]">
            See section ↓
          </div>
        </div>
      </button>
    </div>
  );
}
